import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { patch } from "common/helpers/HTTP";

import { useCampaignContext } from "../context/CampaignContext";
import useUpdateCampaign from "./useUpdateCampaign";

import type {
  CampaignFlow,
  FlowInmailTemplate,
  FlowNoteTemplate,
} from "../types";

async function updateFlowRequest(
  workspaceId: string,
  campaignId: string,
  flowId: string,
  updates: Partial<CampaignFlow>,
): Promise<CampaignFlow> {
  return (
    await patch(
      `workspaces/${workspaceId}/campaigns/${campaignId}/flows/${flowId}`,
      updates,
    )
  ).flow;
}

export default function useCampaignFlow() {
  const queryClient = useQueryClient();
  const { id: campaignId } = useCampaignContext();

  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { campaignsKeys } = getQueryKeys(workspaceId);
  const { updateCachedCampaign } = useUpdateCampaign();

  const { mutateAsync: updateFlow, isPending: isUpdatingFlow } = useMutation({
    mutationFn: ({
      flowId,
      updates,
    }: {
      flowId: string;
      updates: Partial<CampaignFlow>;
    }) => updateFlowRequest(workspaceId, campaignId, flowId, updates),
    onMutate: async ({ flowId, updates }) => {
      await queryClient.cancelQueries({
        queryKey: campaignsKeys.details(campaignId),
      });

      const previousCampaign = queryClient.getQueryData(
        campaignsKeys.details(campaignId),
      );

      updateCachedCampaign(campaignId, (draftCampaign) => {
        const flowIndex = draftCampaign.flows.findIndex(
          (flow) => flow.id === flowId,
        );

        draftCampaign.flows[flowIndex] = {
          ...draftCampaign.flows[flowIndex],
          ...updates,
        };
      });

      return { previousCampaign };
    },
    onError: (_, __, { previousCampaign }) => {
      queryClient.setQueryData(
        campaignsKeys.details(campaignId),
        previousCampaign,
      );

      toast.error(`Error updating flow`);
    },
  });

  function addEmptyFlowTemplate(flowId: string) {
    updateCachedCampaign(campaignId, (draftCampaign) => {
      const flowIndex = draftCampaign.flows.findIndex(
        (flow) => flow.id === flowId,
      );

      const noteTemplate: FlowNoteTemplate = {
        edited: false,
        message: "",
        original_id: null,
        type: "NOTE_TEMPLATE",
        ai_descriptors: {},
      };

      const inmailTemplate: FlowInmailTemplate = {
        ...noteTemplate,
        subject: "",
        type: "INMAIL_TEMPLATE",
      };

      draftCampaign.flows[flowIndex].template =
        draftCampaign.flows[flowIndex].type === "PRIMARY_CONNECT"
          ? noteTemplate
          : inmailTemplate;
    });
  }

  return { addEmptyFlowTemplate, updateFlow, isUpdatingFlow };
}
